import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Badge, Button, Form, InputGroup, Pagination, Table } from "react-bootstrap";
import { FaChevronDown, FaChevronUp, FaSearch, FaStar, FaTimes, FaTrash } from "react-icons/fa";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { catalogApi } from "../../api/services";
import { getErrorMessage } from "../../api/client";
import {
  buildPageItems,
  paginateItems,
} from "../../utils/adminOrders";
import {
  REVIEW_PAGE_SIZES,
  REVIEW_SORT_OPTIONS,
  filterAndSortReviews,
} from "../../utils/adminReviews";
import { TableSkeleton } from "../../components/Skeleton";
import "./ManagePages.css";

const DEFAULT_FILTERS = {
  search: "",
  rating: "all",
  approval: "all",
  dateFrom: "",
  dateTo: "",
  sortBy: "newest",
};

function productLabel(product) {
  if (!product) return "Deleted product";
  if (typeof product === "string") return product;
  return product.name || "Untitled product";
}

function productSlug(product) {
  if (!product || typeof product !== "object") return null;
  return product.slug || null;
}

const ManageReviews = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await catalogApi.adminReviews();
      setReviews(res.data.data.reviews || []);
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to load reviews"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filteredReviews = useMemo(
    () => filterAndSortReviews(reviews, filters),
    [reviews, filters]
  );

  const pagination = useMemo(
    () => paginateItems(filteredReviews, page, pageSize),
    [filteredReviews, page, pageSize]
  );

  useEffect(() => {
    if (page > pagination.totalPages) setPage(pagination.totalPages);
  }, [page, pagination.totalPages]);

  const hasActiveFilters =
    filters.search.trim() ||
    filters.rating !== "all" ||
    filters.approval !== "all" ||
    filters.dateFrom ||
    filters.dateTo ||
    filters.sortBy !== "newest";

  const updateFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
    setExpandedId(null);
  };

  const clearFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setPage(1);
    setExpandedId(null);
  };

  const toggleApproval = async (review) => {
    setBusyId(review._id);
    try {
      const res = await catalogApi.updateReview(review._id, {
        isApproved: !review.isApproved,
      });
      const updated = res.data.data.review;
      setReviews((prev) =>
        prev.map((r) => (r._id === updated._id ? { ...r, ...updated } : r))
      );
      toast.success(updated.isApproved ? "Review approved" : "Review hidden");
    } catch (error) {
      toast.error(getErrorMessage(error, "Could not update review"));
    } finally {
      setBusyId(null);
    }
  };

  const deleteReview = async (review) => {
    const ok = window.confirm(
      `Delete review by ${review.name || "this customer"}? This cannot be undone.`
    );
    if (!ok) return;

    setBusyId(review._id);
    try {
      await catalogApi.deleteReview(review._id);
      setReviews((prev) => prev.filter((r) => r._id !== review._id));
      if (expandedId === review._id) setExpandedId(null);
      toast.success("Review deleted");
    } catch (error) {
      toast.error(getErrorMessage(error, "Could not delete review"));
    } finally {
      setBusyId(null);
    }
  };

  const pageItems = buildPageItems(pagination.page, pagination.totalPages);

  return (
    <div className="manage-page">
      <header className="manage-head">
        <div>
          <h1>Product reviews</h1>
          <p>Search, filter, hide, or delete customer product reviews.</p>
        </div>
        <span className="manage-count">
          {filteredReviews.length === reviews.length
            ? `${reviews.length} total`
            : `${filteredReviews.length} of ${reviews.length}`}
        </span>
      </header>

      {!loading && reviews.length > 0 && (
        <div className="manage-toolbar">
          <InputGroup className="manage-search">
            <InputGroup.Text>
              <FaSearch />
            </InputGroup.Text>
            <Form.Control
              type="search"
              placeholder="Search product, name, email, comment…"
              value={filters.search}
              onChange={(e) => updateFilter("search", e.target.value)}
            />
          </InputGroup>

          <Form.Select
            className="manage-filter"
            value={filters.rating}
            onChange={(e) => updateFilter("rating", e.target.value)}
          >
            <option value="all">All ratings</option>
            {[5, 4, 3, 2, 1].map((n) => (
              <option key={n} value={String(n)}>
                {n} star{n === 1 ? "" : "s"}
              </option>
            ))}
          </Form.Select>

          <Form.Select
            className="manage-filter"
            value={filters.approval}
            onChange={(e) => updateFilter("approval", e.target.value)}
          >
            <option value="all">All visibility</option>
            <option value="approved">Approved</option>
            <option value="hidden">Hidden</option>
          </Form.Select>

          <Form.Control
            type="date"
            className="manage-filter manage-date"
            value={filters.dateFrom}
            onChange={(e) => updateFilter("dateFrom", e.target.value)}
          />
          <Form.Control
            type="date"
            className="manage-filter manage-date"
            value={filters.dateTo}
            onChange={(e) => updateFilter("dateTo", e.target.value)}
          />

          <Form.Select
            className="manage-filter"
            value={filters.sortBy}
            onChange={(e) => updateFilter("sortBy", e.target.value)}
          >
            {REVIEW_SORT_OPTIONS.map(({ value, label }) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Form.Select>

          <Form.Select
            className="manage-filter manage-page-size"
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(1);
            }}
          >
            {REVIEW_PAGE_SIZES.map((size) => (
              <option key={size} value={size}>
                Show {size}
              </option>
            ))}
          </Form.Select>

          {hasActiveFilters && (
            <Button
              variant="outline-secondary"
              size="sm"
              className="manage-clear-btn"
              onClick={clearFilters}
            >
              <FaTimes /> Clear
            </Button>
          )}
        </div>
      )}

      {loading ? (
        <TableSkeleton rows={8} cols={6} />
      ) : reviews.length === 0 ? (
        <p className="manage-empty">No reviews yet.</p>
      ) : filteredReviews.length === 0 ? (
        <div className="manage-empty">
          <p>No reviews match your search or filters.</p>
          <Button variant="link" onClick={clearFilters}>
            Clear filters
          </Button>
        </div>
      ) : (
        <>
          <div className="manage-card table-responsive">
            <Table hover className="manage-table mb-0">
              <thead>
                <tr>
                  <th />
                  <th>Date</th>
                  <th>Product</th>
                  <th>Customer</th>
                  <th>Rating</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pagination.items.map((review) => {
                  const open = expandedId === review._id;
                  const slug = productSlug(review.product);
                  const busy = busyId === review._id;
                  return (
                    <React.Fragment key={review._id}>
                      <tr className={open ? "is-expanded" : undefined}>
                        <td>
                          <Button
                            variant="link"
                            size="sm"
                            className="manage-expand-btn"
                            onClick={() =>
                              setExpandedId(open ? null : review._id)
                            }
                            aria-label={open ? "Collapse" : "Expand"}
                          >
                            {open ? <FaChevronUp /> : <FaChevronDown />}
                          </Button>
                        </td>
                        <td>
                          {new Date(review.createdAt).toLocaleDateString()}
                        </td>
                        <td>
                          {slug ? (
                            <Link
                              to={`/products/${slug}`}
                              target="_blank"
                              rel="noreferrer"
                            >
                              {productLabel(review.product)}
                            </Link>
                          ) : (
                            productLabel(review.product)
                          )}
                        </td>
                        <td>
                          <div>{review.name}</div>
                          <small className="text-muted">{review.email}</small>
                        </td>
                        <td>
                          <span className="d-inline-flex align-items-center gap-1">
                            <FaStar className="text-warning" />
                            {review.rating}
                          </span>
                        </td>
                        <td>
                          <Badge bg={review.isApproved ? "success" : "secondary"}>
                            {review.isApproved ? "Approved" : "Hidden"}
                          </Badge>
                        </td>
                        <td>
                          <div className="d-flex flex-wrap gap-2">
                            <Button
                              size="sm"
                              variant="outline-secondary"
                              disabled={busy}
                              onClick={() => toggleApproval(review)}
                            >
                              {review.isApproved ? "Hide" : "Approve"}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline-danger"
                              disabled={busy}
                              onClick={() => deleteReview(review)}
                            >
                              <FaTrash /> Delete
                            </Button>
                          </div>
                        </td>
                      </tr>
                      {open && (
                        <tr className="manage-order-details-row">
                          <td colSpan={7}>
                            <div className="manage-details-panel">
                              <p className="mb-2">
                                <strong>Full review</strong>
                              </p>
                              <p className="mb-0" style={{ whiteSpace: "pre-wrap" }}>
                                {review.comment || "—"}
                              </p>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </Table>
          </div>

          {pagination.totalPages > 1 && (
            <div className="manage-pagination">
              <Pagination className="mb-0">
                <Pagination.Prev
                  disabled={pagination.page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                />
                {pageItems.map((item, idx) =>
                  item === "…" ? (
                    <Pagination.Ellipsis key={`e-${idx}`} disabled />
                  ) : (
                    <Pagination.Item
                      key={item}
                      active={item === pagination.page}
                      onClick={() => setPage(item)}
                    >
                      {item}
                    </Pagination.Item>
                  )
                )}
                <Pagination.Next
                  disabled={pagination.page >= pagination.totalPages}
                  onClick={() =>
                    setPage((p) => Math.min(pagination.totalPages, p + 1))
                  }
                />
              </Pagination>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ManageReviews;
